import React, { createContext, useContext, useState, useCallback } from 'react';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [globalLoading, setGlobalLoading] = useState(false);

  const setLoading = useCallback((key, isLoading, message = '') => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: isLoading ? { loading: true, message } : undefined,
    }));
  }, []);

  const isLoading = useCallback(
    key => {
      return loadingStates[key]?.loading || false;
    },
    [loadingStates]
  );

  const getLoadingMessage = useCallback(
    key => {
      return loadingStates[key]?.message || '';
    },
    [loadingStates]
  );

  const clearLoading = useCallback(key => {
    setLoadingStates(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  }, []);

  const clearAllLoading = useCallback(() => {
    setLoadingStates({});
    setGlobalLoading(false);
  }, []);

  const withLoading = useCallback(
    async (key, asyncFunction, message = 'Loading...') => {
      setLoading(key, true, message);
      try {
        const result = await asyncFunction();
        return result;
      } finally {
        clearLoading(key);
      }
    },
    [setLoading, clearLoading]
  );

  const hasAnyLoading = Object.keys(loadingStates).length > 0 || globalLoading;

  const value = {
    // State
    loadingStates,
    globalLoading,
    hasAnyLoading,

    // Actions
    setLoading,
    setGlobalLoading,
    isLoading,
    getLoadingMessage,
    clearLoading,
    clearAllLoading,
    withLoading,
  };

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};
